import { useRef, useState } from 'react';
import { RiCloseLine, RiLoader4Line } from '@remixicon/react';

const VerifyCodeStep = ({ maskedPhone, onResend, onVerified, onClose }) => {
  const inputsRef = useRef([]);
  const [digits, setDigits] = useState(['', '', '', '', '', '']);
  const [verifying, setVerifying] = useState(false);
  const [resending, setResending] = useState(false);

  const runVerify = async (code) => {
    if (!onVerified || verifying) return;
    setVerifying(true);
    try {
      await onVerified(code);
    } catch {
      // Clear digits so the user can retry; parent shows the error toast.
      setDigits(['', '', '', '', '', '']);
      inputsRef.current[0]?.focus();
    } finally {
      setVerifying(false);
    }
  };

  const handleChange = (index, value) => {
    if (verifying) return;
    if (!/^\d?$/.test(value)) return;
    const next = [...digits];
    next[index] = value;
    setDigits(next);
    if (value && index < 5) inputsRef.current[index + 1]?.focus();
    if (next.every((d) => d !== '')) runVerify(next.join(''));
  };

  const handleKeyDown = (index, e) => {
    if (verifying) return;
    if (e.key === 'Backspace') {
      if (digits[index] === '' && index > 0) {
        inputsRef.current[index - 1]?.focus();
      } else {
        const next = [...digits];
        next[index] = '';
        setDigits(next);
      }
    } else if (e.key === 'ArrowLeft' && index > 0) {
      inputsRef.current[index - 1]?.focus();
    } else if (e.key === 'ArrowRight' && index < 5) {
      inputsRef.current[index + 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    if (verifying) return;
    const paste = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (paste.length === 6) {
      setDigits(paste.split(''));
      inputsRef.current[5]?.focus();
      e.preventDefault();
      runVerify(paste);
    }
  };

  const handleResend = async () => {
    if (resending || verifying) return;
    setResending(true);
    try {
      await onResend?.();
    } finally {
      setResending(false);
    }
  };

  return (
    <>
      <div className="flex items-center justify-between px-6 pt-5 pb-1">
        <h3 className="text-lg font-semibold text-gray-900">Update phone number</h3>
        <button
          type="button"
          onClick={onClose}
          className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
          aria-label="Close"
        >
          <RiCloseLine className="w-5 h-5" />
        </button>
      </div>
      <div className="px-6 pt-2 pb-6">
        <p className="text-sm text-gray-700">
          Enter the 6-digit code we sent to{' '}
          <span className="font-medium">{maskedPhone}</span> into the field below.
        </p>
        <p className="text-sm text-gray-700 mt-3">Code will expire in 2 minutes.</p>

        <div className="flex gap-2 mt-5">
          {digits.map((digit, idx) => (
            <input
              key={idx}
              ref={(el) => (inputsRef.current[idx] = el)}
              type="text"
              inputMode="numeric"
              maxLength={1}
              value={digit}
              onChange={(e) => handleChange(idx, e.target.value)}
              onKeyDown={(e) => handleKeyDown(idx, e)}
              onPaste={handlePaste}
              disabled={verifying}
              className={`w-12 h-14 sm:w-14 sm:h-16 text-center text-xl font-medium border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                verifying ? 'bg-gray-50 text-gray-400 cursor-not-allowed' : ''
              }`}
              autoFocus={idx === 0}
            />
          ))}
        </div>

        {verifying && (
          <div className="flex items-center gap-2 mt-4 text-sm text-gray-600">
            <RiLoader4Line className="w-4 h-4 animate-spin" />
            Verifying…
          </div>
        )}

        <p className="text-sm text-gray-700 mt-5">
          Didn't receive code?{' '}
          <button
            type="button"
            onClick={handleResend}
            disabled={resending || verifying}
            className={`font-semibold ${
              resending || verifying
                ? 'text-gray-400 cursor-not-allowed'
                : 'text-primary-700 hover:underline'
            }`}
          >
            {resending ? 'Resending…' : 'Resend Code'}
          </button>
        </p>

        <p className="text-xs text-gray-500 mt-4 leading-relaxed">
          By tapping 'Resend Code', you will receive a new text message to that number to verify your account. Message and data rates apply.
        </p>
      </div>
    </>
  );
};

export default VerifyCodeStep;
